import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { athleteGetMessages, athleteSendMessage, athleteTypingPing } from "../../lib/api";
import { useAthleteAuth } from "../../lib/athleteAuth";
import ChatConversation from "../../components/ChatConversation";
import AthleteNav from "../../components/athlete/AthleteNav";

const POLL_MS = 5 * 1000;

export default function AthleteMessagesPage() {
  const { athlete, loading, logout } = useAthleteAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null); // { messages, other_typing }

  useEffect(() => {
    if (!loading && !athlete) {
      navigate("/athlete/login?next=%2Fathlete%2Fmessages", { replace: true });
    }
  }, [athlete, loading, navigate]);

  useEffect(() => {
    if (!athlete) return;
    const load = () =>
      athleteGetMessages()
        .then(setData)
        .catch(() => setData((prev) => prev || { messages: [], other_typing: false }));
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [athlete]);

  if (loading || !athlete) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="h-screen flex flex-col bg-[var(--tir-ivory)]"
      data-testid="athlete-messages-page"
    >
      <header className="border-b border-[var(--tir-border)] bg-white shrink-0">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/athlete/home"
              className="text-[var(--tir-muted)] hover:text-[var(--tir-navy)]"
              data-testid="athlete-messages-back"
              aria-label="Back to home"
            >
              <ArrowLeft size={16} />
            </Link>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-navy)]">
                Messages
              </div>
              <div className="font-serif-display text-2xl text-[var(--tir-ink)] mt-0.5 truncate">
                Your Coach
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            className="text-xs tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] inline-flex items-center gap-1.5"
            data-testid="athlete-logout"
          >
            <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>
      <AthleteNav />
      <div className="flex-1 min-h-0 max-w-3xl w-full mx-auto pb-16 md:pb-0">
        {data === null ? (
          <div className="text-center text-sm text-[var(--tir-muted)] italic py-10">
            Loading messages…
          </div>
        ) : (
          <ChatConversation
            messages={data.messages || []}
            side="athlete"
            onSend={athleteSendMessage}
            otherTyping={!!data.other_typing}
            otherLabel="Coach"
            onTyping={athleteTypingPing}
            contextLink={(m) =>
              m.program_id
                ? `/athlete/programs/${m.program_id}?week=${m.week_number}&workout=${m.workout_id}`
                : null
            }
          />
        )}
      </div>
    </div>
  );
}
